// Storm Call List widget — the shared hunting pool (Ship 1, Aug 25, 2026).
//
// Greg's framing: "a place where anyone can go and call/text people we have
// interacted with in the past, with new storm date, try to make something
// happen." The unassigned pool is open to every rep; booking revives the dead
// deal with the new date of loss and the BOOKER gets the canvasser credit.
// The page got 6 visits its second week while 2,000+ leads sat waiting — the
// pool needs to meet people on home.
//
// ALL reads go through the SECURITY DEFINER RPC wrappers in lib/recanvass —
// never direct deals selects (an RM's deals RLS is blind to other reps' dead
// deals; the RPCs exist exactly for this). Booking deep-links to /call-list —
// the revive flow with its confirms lives there, the widget performs no writes.
//
// Fetch is deferred until the widget scrolls into view (below-the-fold on
// most boards; keep first paint light on cellular).
//
// ACCESS CONTROL (Aug 31, 2026): the board can be switched off for the field
// tier, and reps can be limited to approved ZIPs. The summary RPC already
// returns counts scoped to what the caller may open, so the "N waiting" figure
// stays honest on its own — but a paused rep must not be left staring at a
// silent zero, so the paused state gets its own card.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchRecanvassLeads,
  fetchRecanvassSummary,
  type RecanvassLead,
  type RecanvassSummary,
} from "../../../lib/recanvass";
import { logCardClick } from "../../../lib/uiEvents";

function fmtStormDate(iso: string): string {
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00Z");
  return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

export default function CallListWidget() {
  const [summary, setSummary] = useState<RecanvassSummary | null>(null);
  const [leads, setLeads] = useState<RecanvassLead[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Lazy-load: fetch only once the widget is (nearly) on screen.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const [sum, rows] = await Promise.all([
          fetchRecanvassSummary(),
          fetchRecanvassLeads({ status: ["new"], sort: "size_desc", limit: 3 }),
        ]);
        if (cancelled) return;
        setSummary(sum);
        setLeads(rows.filter((r) => !!r.homeowner_phone));
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, loaded]);

  return (
    <div ref={boxRef} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span>☎️</span>
        <span className="font-bold text-sm text-bardin-ink">Storm Call List</span>
        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-50 text-bardin-orange">
          Open pool
        </span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {summary?.paused_for_me
            ? "paused"
            : summary
              ? `${summary.unassigned.toLocaleString()} waiting`
              : loaded ? "" : "…"}
        </span>
      </div>

      <div className="text-[11px] text-slate-500 mb-2">
        Past contacts with a <b className="text-bardin-ink">new hail date</b> after their deal died.
        Anyone can call, text, and book — booking revives the deal and credits you.
        {summary?.zip_limited && !summary.paused_for_me && (
          <>
            {" "}
            <b className="text-bardin-ink">Approved ZIPs only:</b>{" "}
            {summary.allowed_zips.join(", ")}.
          </>
        )}
      </div>

      {summary?.paused_for_me ? (
        <div className="text-xs text-slate-500 py-2">
          ⏸ Paused by your ASM — the pool is closed right now. The leads are still here.
        </div>
      ) : failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the pool — open the Call List instead.</div>
      ) : !loaded ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : leads.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">Pool is empty right now — it refreshes nightly.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {leads.map((l) => (
            <div key={l.lead_id} className="flex items-center gap-2 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-bardin-ink truncate">
                  {l.homeowner_name ?? "—"}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {[l.property_city, l.property_zip].filter(Boolean).join(" · ")}
                </div>
              </div>
              <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[10px] font-extrabold whitespace-nowrap">
                ⛈ {l.hail_size_in}″ · {fmtStormDate(l.hail_occurred_at)}
              </span>
              <div className="flex gap-1 flex-none">
                {l.homeowner_phone && (
                  <>
                    <a
                      href={`tel:${l.homeowner_phone}`}
                      onClick={() => logCardClick("widget_calllist_call")}
                      className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-xs hover:border-bardin-orange"
                      title="Call"
                    >
                      📞
                    </a>
                    <a
                      href={`sms:${l.homeowner_phone}`}
                      onClick={() => logCardClick("widget_calllist_text")}
                      className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-xs hover:border-bardin-orange"
                      title="Text"
                    >
                      💬
                    </a>
                  </>
                )}
                <Link
                  to="/call-list"
                  onClick={() => logCardClick("widget_calllist_book")}
                  className="px-2 h-7 rounded-lg bg-orange-50 text-bardin-orange flex items-center text-[10px] font-extrabold hover:bg-orange-100"
                  title="Book it on the Call List — revives the deal with the new date of loss"
                >
                  📅 Book
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Coverage watch — renders only for viewers the RPC says can assign
          (ASM/managers): is the pool getting worked, with batch-assign a tap
          away. Assignment is optional; the pool stays open to everyone. */}
      {summary?.can_assign && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5 text-[10px] font-bold">
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{summary.assigned} assigned</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{summary.working} working</span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{summary.appt_set} booked</span>
          <Link
            to="/call-list"
            onClick={() => logCardClick("widget_calllist_assign")}
            className="ml-auto text-bardin-orange"
          >
            Assign a batch →
          </Link>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-slate-100">
        {summary && summary.appt_set > 0 && !summary.can_assign && (
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            {summary.appt_set} booked so far
          </span>
        )}
        <Link
          to="/call-list"
          onClick={() => logCardClick("widget_calllist_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Open Call List ›
        </Link>
      </div>
    </div>
  );
}
